import {
  Building2,
  Eye,
  EyeOff,
  CircleDollarSign,
  Database,
  FilterX,
  LineChart,
  Plus,
  Search,
  Tag,
  UsersRound,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  NOME_ENQUADRAMENTO,
  NOME_TIPO_SOCIETARIO,
  nomeDe,
  sociosVigentes,
  TIPOS_SOCIETARIOS,
  type Pagina,
  type Pessoa,
  type Sociedade,
  type TipoSocietario,
} from '@social/shared';
import { Abas, type Aba } from '../componentes/abas';
import { AlternadorVisao, useVisao } from '../componentes/alternador-visao';
import { CartaoKpi, LinhaKpi } from '../componentes/cartao-kpi';
import { GavetaFiltros } from '../componentes/gaveta-filtros';
import { PaginaShell } from '../componentes/pagina-shell';
import { SeletorOpcao, type OpcaoSeletor } from '../componentes/seletor-opcao';
import { PeriodoRapido, type Periodo } from '../componentes/periodo-rapido';
import { useColunas, useColunasOcultas } from '../componentes/seletor-colunas';
import { Tabela, type Coluna, type Ordenacao } from '../componentes/tabela';
import { Spinner } from '../componentes/carregando';
import {
  ACAO_PRIMARIA_LINK,
  Aviso,
  Botao,
  CONTROLE_BARRA,
  Entrada,
} from '../componentes/ui';
import { comEspera } from '../lib/ocupado';
import { api } from '../lib/sessao';
import { CartaoSociedade } from './sociedade-cartao';
import { MenuOpcoesSociedades } from './sociedades-menu-opcoes';
import { ResumoPorTipo } from './sociedades-resumo';
import {
  classeSeloTipo,
  cnpjBR,
  dataBR,
  limitesDoPeriodo,
  moeda,
  paraResumo,
  semRegistro,
  sociosAtuais,
  type SociedadeResumo,
} from './sociedade-comum';
import {
  camposDeFiltroDeSociedade,
  SEM_FILTRO,
  type FiltrosAvancados,
} from './sociedades-filtros';

/*
  As opções do seletor de tipo.

  A `descricao` diz de quantos sócios cada tipo precisa — é a dúvida real de
  quem escolhe, e o `<select>` nativo não comporta uma segunda linha. Os textos
  saem das regras que o validador aplica (`packages/validador/src/regras/`), e
  não de conhecimento solto aqui.
*/
const DESCRICAO_TIPO: Record<TipoSocietario, string> = {
  SLU: 'Exatamente 1 sócio',
  LTDA: '2 ou mais sócios',
  EI: 'Requerimento, não contrato social',
  SS: '2 ou mais — atividade intelectual',
  SA: 'Acionistas, estatuto e ações',
  NC: 'Só pessoas físicas — responsabilidade ilimitada',
  CS: 'Comanditado e comanditário, ao menos um de cada',
};

const OPCOES_TIPO: readonly OpcaoSeletor<TipoSocietario | ''>[] = [
  { valor: '', rotulo: 'Todos os tipos', Icone: Tag },
  ...TIPOS_SOCIETARIOS.map((t) => ({
    valor: t,
    rotulo: NOME_TIPO_SOCIETARIO[t],
    descricao: DESCRICAO_TIPO[t],
    Icone: Building2,
  })),
];

const ABAS: readonly Aba[] = [
  { id: 'dados', rotulo: 'Dados', icone: Database },
  { id: 'resumo', rotulo: 'Resumo', icone: LineChart },
];

/** `'50000,00'` ou `'50000.00'` → `5000000` (centavos). `undefined` quando vazio ou inválido. */
function centavosDe(texto: string | undefined): number | undefined {
  if (!texto) return undefined;
  const n = Number(texto.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
}

/**
 * Consulta de sociedades.
 *
 * Segue o molde da consulta de pedidos do Adonai. As decisões dele que foram
 * trazidas, e por quê:
 *
 *  - **Uma leva grande, filtrada na memória.** A listagem não tem parâmetro de
 *    busca (dívida D10) — buscar 200 de uma vez e filtrar no cliente dá, de
 *    graça, todos os recortes que o servidor não oferece. A MESMA leva de
 *    pessoas (também até 200) resolve nome de sócio, que `Sociedade.socios`
 *    só guarda como `pessoaId` — ver o comentário de `SociedadeResumo`.
 *  - **Abas sobre a MESMA leva.** Trocar de aba não refaz o filtro, então o
 *    total do resumo bate com a soma da lista por construção.
 *  - **Início e fim como campos PRÓPRIOS**, ao lado do preset, dentro do
 *    próprio `PeriodoRapido`.
 *  - **KPI que some.** O olho esconde os indicadores para quem não precisa
 *    deles o tempo todo.
 *  - **Filtros avançados em gaveta, aplicados no botão.** Enquadramento e mais
 *    oito campos não cabem na barra, e aplicar a cada tecla faria a lista
 *    piscar antes de chegar onde a pessoa queria — ver a regra de
 *    barra-vs-gaveta em `pagina-shell.tsx`. **Tipo é exceção**: filtrar por
 *    tipo societário é trabalho de todo dia para um contador, então fica na
 *    barra, ao vivo, junto de busca e período.
 */
export function Sociedades(): JSX.Element {
  const [aba, setAba] = useState('dados');
  // A busca tem DOIS estados de proposito: `busca` e o que esta digitado,
  // `buscaAplicada` e o que esta filtrando. Antes eram um so, e cada tecla
  // refazia o recorte inteiro sobre a leva de 200 — recalculando KPIs,
  // reordenando e repintando a tabela a cada letra de "Comercial". Agora o
  // recorte roda quando a pessoa confirma (botao Consultar ou Enter).
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  // Vive fora da gaveta, ao lado de busca e período — ver a nota da função.
  const [tipo, setTipo] = useState<TipoSocietario | ''>('');
  // `dias: 0` é "todo o período": uma consulta cadastral não nasce filtrada
  // pelo tempo do jeito que uma fila operacional nasce — ver o comentário de
  // `rotuloVazio` em `PeriodoRapido`.
  const [periodo, setPeriodo] = useState<Periodo>({ dias: 0 });
  const [ordenacao, setOrdenacao] = useState<Ordenacao | null>(null);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [filtros, setFiltros] = useState<FiltrosAvancados>(SEM_FILTRO);
  const [kpisVisiveis, setKpisVisiveis] = useState(true);
  const ocultas = useColunasOcultas('sociedades');
  const alternarColuna = useColunas((e) => e.alternar);
  const restaurarColunas = useColunas((e) => e.restaurar);
  const [visao, setVisao] = useVisao('sociedades.consulta');

  const sociedadesQuery = useQuery({
    queryKey: ['sociedades', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Sociedade>>('/sociedades?tamanho=200'),
  });

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });

  const nomePorPessoaId = useMemo(() => {
    const mapa = new Map<string, string>();
    for (const p of pessoasQuery.data?.itens ?? []) mapa.set(p.id, nomeDe(p));
    return mapa;
  }, [pessoasQuery.data]);

  const resumos = useMemo(
    () => (sociedadesQuery.data?.itens ?? []).map((s) => paraResumo(s, nomePorPessoaId)),
    [sociedadesQuery.data, nomePorPessoaId],
  );

  const janela = useMemo(() => limitesDoPeriodo(periodo), [periodo]);

  const visiveis = useMemo(() => {
    // Tudo o que nao depende da LINHA e normalizado uma vez, aqui fora: com
    // `toLowerCase()` dentro do laco, cada um dos 200 registros refazia a
    // mesma conversao do mesmo termo.
    const termo = buscaAplicada.trim().toLowerCase();
    const digitos = buscaAplicada.replace(/\D/g, '');
    const cnpjFiltro = filtros.cnpj?.replace(/\D/g, '') ?? '';
    const nire = filtros.nire?.toLowerCase() ?? '';
    const municipio = filtros.municipio?.toLowerCase() ?? '';
    const uf = filtros.uf?.trim().toLowerCase() ?? '';
    const socio = filtros.socio?.toLowerCase() ?? '';
    const capitalDe = centavosDe(filtros.capitalDe);
    const capitalAte = centavosDe(filtros.capitalAte);
    const { enquadramento, semRegistro: recorteRegistro } = filtros;

    return resumos.filter((s) => {
      // Ordem deliberada: o que e barato e corta muito vem primeiro.
      if (s.inicioAtividades < janela.de || s.inicioAtividades > janela.ate) return false;
      if (tipo && s.tipo !== tipo) return false;
      if (enquadramento && s.enquadramento !== enquadramento) return false;
      if (capitalDe !== undefined && s.capitalCentavos < capitalDe) return false;
      if (capitalAte !== undefined && s.capitalCentavos > capitalAte) return false;
      if (cnpjFiltro && !(s.cnpj ?? '').includes(cnpjFiltro)) return false;
      if (uf && s.sede.uf.toLowerCase() !== uf) return false;
      if (nire && !(s.nire ?? '').toLowerCase().includes(nire)) return false;
      if (municipio && !(s.sede.municipio ?? '').toLowerCase().includes(municipio))
        return false;
      if (socio && !s.nomesSocios.some((n) => n.toLowerCase().includes(socio)))
        return false;

      if (
        termo &&
        !`${s.nomeEmpresarial} ${s.nomeFantasia ?? ''}`.toLowerCase().includes(termo) &&
        !(digitos !== '' && (s.cnpj ?? '').includes(digitos))
      ) {
        return false;
      }

      if (recorteRegistro) {
        const sem = semRegistro(s);
        if (recorteRegistro === 'sim' ? !sem : sem) return false;
      }

      return true;
    });
  }, [resumos, janela, buscaAplicada, tipo, filtros]);

  // A leva inteira já está em memória (o recorte é feito aqui, não no
  // servidor — ver o comentário de `pagina` lá embaixo), então paginar é
  // fatiar. `useMemo` porque a fatia é recalculada a cada render do filtro, e
  // `slice` numa leva de 200 não precisa rodar quando nada mudou.
  const ordenados = useMemo(() => {
    if (!ordenacao) return visiveis;
    const sinal = ordenacao.direcao === 'asc' ? 1 : -1;
    return [...visiveis].sort((a, b) => {
      const x = valorOrdenavel(a, ordenacao.chave);
      const y = valorOrdenavel(b, ordenacao.chave);
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * sinal;
      return String(x ?? '').localeCompare(String(y ?? '')) * sinal;
    });
  }, [visiveis, ordenacao]);

  const kpis = useMemo(() => {
    const total = ordenados.length;
    const capitalTotal = ordenados.reduce((soma, s) => soma + s.capitalCentavos, 0);

    const distintos = new Set<string>();
    let somaSociosAtuais = 0;
    for (const s of ordenados) {
      somaSociosAtuais += sociosAtuais(s);
      for (const p of sociosVigentes(s)) distintos.add(p.pessoaId);
    }

    const porTipo = TIPOS_SOCIETARIOS.map((t) => ({
      tipo: t,
      contagem: ordenados.filter((s) => s.tipo === t).length,
    })).filter((l) => l.contagem > 0);

    return {
      total,
      capitalTotal,
      ticket: total > 0 ? capitalTotal / total : 0,
      sociosDistintos: distintos.size,
      mediaSocios: total > 0 ? somaSociosAtuais / total : 0,
      porTipo,
    };
  }, [ordenados]);

  const colunas: Coluna<SociedadeResumo>[] = [
    {
      chave: 'codigo',
      titulo: 'Código',
      ordenavel: true,
      /*
        Primeira coluna, e é de propósito.

        É o número que a pessoa cita ao telefone e anota no papel — "a
        sociedade 42". Ele existe justamente porque o `id` (uuid) não serve
        para isso, e escondê-lo no fim da linha desfaria metade do ganho.

        `tabular-nums` para os dígitos não dançarem de largura entre linhas, e
        `text-tinta-500` porque ele identifica sem competir com o nome, que é
        o título da linha.
      */
      celula: (s) => <span className="text-tinta-500 tabular-nums">{s.codigo}</span>,
    },
    {
      chave: 'nomeEmpresarial',
      titulo: 'Nome empresarial',
      ordenavel: true,
      // Único texto do link: é o contrato de e2e — o nome empresarial É o
      // texto acessível, sem ícone nem selo dividindo o nome acessível.
      papel: 'titulo',
      celula: (s) => <Link to={`/sociedades/${s.id}`}>{s.nomeEmpresarial}</Link>,
    },
    {
      chave: 'tipo',
      titulo: 'Tipo',
      ordenavel: true,
      celula: (s) => (
        <span className={classeSeloTipo(s.tipo)} title={NOME_TIPO_SOCIETARIO[s.tipo]}>
          {s.tipo}
        </span>
      ),
    },
    {
      chave: 'cnpj',
      titulo: 'CNPJ',
      ordenavel: true,
      ocultarNoCelular: true,
      celula: (s) => cnpjBR(s.cnpj),
    },
    {
      chave: 'enquadramento',
      titulo: 'Enquadramento',
      ordenavel: true,
      ocultarNoCelular: true,
      celula: (s) => NOME_ENQUADRAMENTO[s.enquadramento],
    },
    {
      chave: 'capitalCentavos',
      titulo: 'Capital',
      ordenavel: true,
      alinhamento: 'direita',
      // O capital é o número que se procura — no card ele fica no canto,
      // como fecho, em vez de virar mais um par rótulo/valor.
      papel: 'destaque',
      celula: (s) => <span className="tabular-nums">{moeda(s.capitalCentavos)}</span>,
    },
    {
      chave: 'socios',
      titulo: 'Sócios',
      ordenavel: true,
      alinhamento: 'direita',
      ocultarNoCelular: true,
      celula: (s) => <span className="tabular-nums">{sociosAtuais(s)}</span>,
    },
    {
      chave: 'inicioAtividades',
      titulo: 'Início de atividades',
      ordenavel: true,
      ocultarNoCelular: true,
      celula: (s) => dataBR(s.inicioAtividades),
    },
    {
      chave: 'municipio',
      titulo: 'Município/UF',
      ordenavel: true,
      ocultarNoCelular: true,
      celula: (s) => `${s.sede.municipio}/${s.sede.uf}`,
    },
  ];

  const opcoesColunas = colunas.map((c) => ({ chave: c.chave, titulo: c.titulo }));

  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / tamanhoPagina));

  // A página vigente pode ter deixado de existir: filtrar de 183 para 8
  // registros com a pessoa na página 7 mostraria uma lista vazia com o rodapé
  // dizendo "página 7 de 1". Corrigir no render (e não num efeito) evita o
  // frame intermediário em que a tela mostra o vazio errado.
  const paginaValida = Math.min(paginaAtual, totalPaginas);

  const daPagina = useMemo(
    () =>
      ordenados.slice((paginaValida - 1) * tamanhoPagina, paginaValida * tamanhoPagina),
    [ordenados, paginaValida, tamanhoPagina],
  );

  // Um só booleano governa KPI, abas e o botão de indicadores no menu — os
  // três só existem quando há o que resumir. Ver a regra de proporção em
  // `pagina-shell.tsx`.
  const haDados = kpis.total > 0;

  // Some no botão da própria mensagem vazia — só faz sentido quando o vazio é
  // efeito de um recorte, nunca na ausência real de dado (ver `vazio` abaixo).
  const limparFiltros = () => {
    setBusca('');
    setBuscaAplicada('');
    setTipo('');
    setPeriodo({ dias: 0 });
    setFiltros(SEM_FILTRO);
    // Recomeçar é recomeçar do começo: limpar tudo e continuar na página 3
    // deixaria a pessoa no meio de uma lista que ela acabou de restaurar.
    setPaginaAtual(1);
  };

  // Ha algum recorte valendo? Governa o botao Limpar da barra: um botao que
  // nao limpa nada e ruido permanente na barra de ferramentas.
  const haRecorte =
    buscaAplicada !== '' ||
    busca !== '' ||
    tipo !== '' ||
    periodo.dias > 0 ||
    Boolean(periodo.inicio && periodo.fim) ||
    Object.values(filtros).some((v) => v !== undefined && v !== '');

  // Digitou e ainda nao confirmou. O botao Consultar avisa, senao a pessoa
  // digita, ve a lista parada e conclui que a busca nao funciona.
  const buscaPendente = busca.trim() !== buscaAplicada.trim();

  /*
    Consultar CONSULTA — vai ao servidor, não só reaplica o recorte.

    Antes ele só copiava `busca` para `buscaAplicada`, e o recorte é feito em
    memória sobre a leva já carregada: instantâneo, sem rede. O efeito era um
    botão chamado "Consultar" que não consultava nada — e nenhum indicador de
    espera tinha o que indicar, nem com a rede estrangulada.

    Agora ele também revalida a leva. Duas coisas ganham: o dado fica fresco
    no momento em que a pessoa pede (outro usuário do escritório pode ter
    cadastrado enquanto esta tela estava aberta), e existe uma requisição de
    verdade para o botão acompanhar.

    O `void` é deliberado: o botão não espera a resposta para aplicar o
    recorte. O que já está em memória filtra na hora, e a leva nova entra
    quando chegar — segurar o recorte pela rede deixaria a tela parada com o
    dado que ela já tem.
  */
  const consultando = sociedadesQuery.isFetching || pessoasQuery.isFetching;

  const aplicarBusca = () => {
    setBuscaAplicada(busca);
    setPaginaAtual(1);
    // `comEspera` acende o indicador global: quem clicou está esperando, e o
    // spinner do botão sozinho é pequeno demais para uma consulta que demora.
    void comEspera(
      () => Promise.all([sociedadesQuery.refetch(), pessoasQuery.refetch()]),
      'Consultando…',
    );
  };

  // As duas mensagens de estado vazio — ver docs/DIVIDA_TECNICA.md#d10.
  // A leva tem até 200: se `total` do servidor for maior que o que chegou,
  // a leva está incompleta e a sociedade procurada PODE estar fora dela — a
  // tela não pode dizer "não existe", porque não é verdade. Só quando a leva
  // é tudo (`total <= itens.length`) a mensagem pode ser definitiva.
  //
  // Uma linha, uma ação — ver a regra de proporção em `pagina-shell.tsx`. Só
  // as duas mensagens de RECORTE ganham "Limpar filtros"; a ausência real de
  // dado não tem filtro nenhum para limpar.
  const vazio = useMemo(() => {
    const dados = sociedadesQuery.data;
    if (!dados || dados.itens.length === 0) {
      return {
        titulo: 'Nenhuma sociedade cadastrada ainda.',
        descricao: 'Comece por uma constituição.',
      };
    }
    if (dados.total > dados.itens.length) {
      return {
        titulo: 'Essa busca não sai desta leva.',
        descricao:
          'Pode haver sociedades além das primeiras 200 carregadas — restrinja ou limpe os filtros.',
        acao: (
          <Botao variante="secundaria" onClick={limparFiltros}>
            Limpar filtros
          </Botao>
        ),
      };
    }
    return {
      titulo: 'Nenhuma sociedade encontrada.',
      descricao: 'Tente outro nome, CNPJ ou filtro.',
      acao: (
        <Botao variante="secundaria" onClick={limparFiltros}>
          Limpar filtros
        </Botao>
      ),
    };
  }, [sociedadesQuery.data]);

  return (
    <PaginaShell
      titulo="Sociedades"
      acoes={
        <div className="flex flex-wrap items-center gap-2">
          {/*
            A ação primária da tela — e agora ela LEVA a algum lugar.

            Ficou desabilitada enquanto o cadastro não existia, com o `title`
            dizendo quando chegaria: promessa falsa é pior que ausência. O
            cadastro existe, então o botão vira link de verdade.
          */}
          <Link to="/sociedades/nova" className={ACAO_PRIMARIA_LINK}>
            <Plus className="size-4" aria-hidden />
            Nova sociedade
          </Link>
        </div>
      }
      filtros={
        /*
          Altura igual em todo controle (`h-11`), nenhum rótulo empilhado —
          ver a regra em `pagina-shell.tsx`. `flex-wrap` e não uma linha
          rígida: em tela média a barra não cabe inteira, e sem o wrap ela
          transbordava o painel em vez de quebrar. Quebrando, o que sobra cai
          para a linha de baixo com a mesma altura e o mesmo respiro. Só o que muda o
          RESULTADO fica aqui: busca, período e tipo, mais o botão de filtros.
          Colunas, lista/cards, indicadores e atualizar — que só mudam a
          APARÊNCIA — moram no menu "Mais opções", ao final da barra.

          A LAJE (fundo, borda, raio, padding) não mora aqui: mora no
          `PaginaShell`, em volta deste slot. Tela nova só entrega os
          controles.
        */
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
          {/*
            Campo e botao formam UM gesto, por isso vivem no mesmo grupo com
            gap curto: o Consultar pertence a busca ao lado dele, nao a barra
            inteira. Enter faz o mesmo que o clique — quem digita nao tira a
            mao do teclado para confirmar.
          */}
          <div className="col-span-full flex items-center gap-2 lg:min-w-72 lg:flex-1">
            <div className="relative min-w-0 flex-1">
              <Search
                className="text-tinta-500 pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                aria-hidden
              />
              <Entrada
                aria-label="Buscar sociedade"
                placeholder="Buscar por nome, nome fantasia ou CNPJ…"
                className="pr-9 pl-9"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    aplicarBusca();
                  }
                }}
              />
              {busca && (
                <button
                  type="button"
                  onClick={() => {
                    setBusca('');
                    setBuscaAplicada('');
                  }}
                  aria-label="Limpar a busca"
                  title="Limpar a busca"
                  className="text-tinta-500 hover:text-tinta-900 absolute top-1/2 right-2 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-md transition-colors"
                >
                  <X className="size-4" aria-hidden />
                </button>
              )}
            </div>

            {/*
              Só o ícone, e no mesmo fundo dos outros controles: ele não é a
              ação primária da TELA — essa é "Nova sociedade", que fica em
              destaque no cabeçalho. Um segundo botão cheio na barra disputava
              esse papel.

              Com termo por confirmar, a BORDA e o ícone assumem a cor da
              marca: é o que separa "ainda não consultei" de "consultei e deu
              isso", e sem o texto a cor é o que sobra para dizer isso.
            */}
            <button
              type="button"
              onClick={aplicarBusca}
              disabled={consultando}
              aria-label="Consultar"
              aria-busy={consultando}
              title={
                consultando
                  ? 'Consultando…'
                  : buscaPendente
                    ? 'Consultar com o que foi digitado (ou tecle Enter)'
                    : 'A consulta já está aplicada'
              }
              className={`${CONTROLE_BARRA} w-11 shrink-0 justify-center ${
                buscaPendente && !consultando ? 'border-marca-600 text-marca-600' : ''
              }`}
            >
              {/*
                O spinner mora NO BOTÃO, não num overlay de tela cheia.

                É onde o olho está — a pessoa acabou de clicar ali — e é
                proporcional ao que acontece: a lista continua na tela, legível
                e correta, enquanto a leva nova vem. Cobrir a tela inteira para
                revalidar o que já está certo é o erro que o `CarregandoGlobal`
                evita de propósito (ver o comentário dele).
              */}
              {consultando ? (
                <Spinner tamanho="sm" />
              ) : (
                <Search className="size-4" aria-hidden />
              )}
            </button>
          </div>

          <div className="lg:contents [&_button]:w-full lg:[&_button]:w-auto">
            <PeriodoRapido
              periodo={periodo}
              aoEscolher={(p) => {
                setPeriodo(p);
                setPaginaAtual(1);
              }}
              rotuloVazio="Todo o período"
            />
          </div>

          {/*
          Ideia portada do `icon-select.tsx` do Isotec, no tamanho do nosso
          problema: o ícone identifica o campo, sem rótulo empilhado — o
          `<select>` nativo já mostra o valor escolhido, e o `aria-label`
          cobre leitor de tela.
        */}
          {/*
          `SeletorOpcao` e nao `<select>` nativo: o nativo e desenhado pelo
          sistema operacional — abre uma lista branca que ignora o tema e corta
          "Sociedade em Comandita Simples". O painel aqui tem largura PROPRIA,
          e cada tipo carrega uma linha dizendo de quantos socios ele precisa,
          que e a duvida real de quem escolhe.
        */}
          <div className="lg:contents [&_button]:w-full lg:[&_button]:w-auto">
            <SeletorOpcao<TipoSocietario | ''>
              rotulo="Filtrar por tipo societário"
              IconeGatilho={Tag}
              larguraPainel="w-80"
              valor={tipo}
              aoEscolher={(t) => {
                setTipo(t);
                setPaginaAtual(1);
              }}
              opcoes={OPCOES_TIPO}
            />
          </div>

          {/*
            Sem `Limpar` ao lado, `Filtros` toma a linha inteira em vez de
            deixar meia linha vazia — buraco na grade lê como algo faltando.
          */}
          <div
            className={`lg:contents [&_button]:w-full lg:[&_button]:w-auto ${
              haRecorte ? '' : 'sm:col-span-2'
            }`}
          >
            <GavetaFiltros<FiltrosAvancados>
              valor={filtros}
              vazio={SEM_FILTRO}
              aoAplicar={(f) => {
                setFiltros(f);
                setPaginaAtual(1);
              }}
            >
              {camposDeFiltroDeSociedade}
            </GavetaFiltros>
          </div>

          {/*
            Limpar mora ao lado dos filtros que ele desfaz, e SO existe quando
            ha o que limpar — botao permanentemente inerte e ruido fixo na
            barra. Zera busca, periodo, tipo e a gaveta de uma vez: quem quer
            recomecar quer recomecar inteiro, nao controle a controle.
          */}
          {haRecorte && (
            <button
              type="button"
              onClick={limparFiltros}
              title="Limpar busca, período, tipo e filtros avançados"
              className={`${CONTROLE_BARRA} w-full justify-center px-3 lg:w-auto lg:justify-start`}
            >
              <FilterX className="size-4" aria-hidden />
              Limpar
            </button>
          )}

          {/*
          Lista e cards ficam NA BARRA, como no isotec.

          As duas leituras existem: a lista COMPARA (qual capital é maior? qual
          é mais antiga?) e o card IDENTIFICA (é esta sociedade mesmo?). Trocar
          entre elas é gesto de consulta, não de configuração — não pertence ao
          menu de aparência.
        */}
          {/*
            No celular os três controles de ícone dividem a última linha,
            encostados à direita; `lg:contents` desfaz o grupo no desktop e
            eles voltam a ser itens soltos da barra.
          */}
          <div className="col-span-full flex items-center justify-end gap-2 lg:contents">
            <AlternadorVisao visao={visao} aoTrocar={setVisao} />

            {/*
          O olho fica NA BARRA, fora do menu.

          Ele esconde os valores — capital social, ticket médio — e quem
          precisa disso precisa AGORA: o contador com o cliente ao lado, ou
          compartilhando a tela numa reunião. Um controle de privacidade
          atrás de dois cliques não serve para o que existe.

          É a exceção à regra do chrome colapsado (`pagina-shell.tsx`), e é
          onde o isotec o coloca — ele é o nosso parâmetro de layout.
        */}
            {haDados && (
              <button
                type="button"
                onClick={() => setKpisVisiveis((v) => !v)}
                aria-pressed={!kpisVisiveis}
                aria-label={kpisVisiveis ? 'Esconder valores' : 'Mostrar valores'}
                title={kpisVisiveis ? 'Esconder valores' : 'Mostrar valores'}
                className={`${CONTROLE_BARRA} w-11 justify-center`}
              >
                {kpisVisiveis ? (
                  <Eye className="size-4" aria-hidden />
                ) : (
                  <EyeOff className="size-4" aria-hidden />
                )}
              </button>
            )}

            {/*
          O resto do chrome fica colapsado — ver a regra de proporção em
          `pagina-shell.tsx`. Colunas, lista/cards e atualizar não mudam o
          RESULTADO, só a aparência.
        */}
            <MenuOpcoesSociedades
              opcoesColunas={opcoesColunas}
              colunasOcultas={ocultas}
              aoAlternarColuna={(chave) => alternarColuna('sociedades', chave)}
              aoRestaurarColunas={() => restaurarColunas('sociedades')}
              atualizando={sociedadesQuery.isFetching || pessoasQuery.isFetching}
              aoAtualizar={() => {
                void comEspera(
                  () => Promise.all([sociedadesQuery.refetch(), pessoasQuery.refetch()]),
                  'Atualizando…',
                );
              }}
            />
          </div>
        </div>
      }
    >
      {/*
        Sem abas com a leva filtrada vazia: "Resumo" de zero registro é aba
        vazia — ver a regra de proporção em `pagina-shell.tsx`. A tela mostra
        "Dados" direto, sem custar o clique de uma aba que não tem conteúdo.

        As abas ficam ABAIXO da barra de ferramentas: o `PaginaShell` renderiza
        `filtros` antes de `children`, que é a ordem do isotec.
      */}
      {haDados && <Abas abas={ABAS} ativa={aba} aoEscolher={setAba} />}

      {haDados && kpisVisiveis && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <CartaoKpi
            rotulo="Sociedades"
            valor={String(kpis.total)}
            detalhe="no período"
            icone={Building2}
            tom="neutro"
          >
            {kpis.porTipo.map((l) => (
              <LinhaKpi
                key={l.tipo}
                rotulo={NOME_TIPO_SOCIETARIO[l.tipo]}
                valor={String(l.contagem)}
                proporcao={kpis.total ? l.contagem / kpis.total : 0}
                tom="marca"
              />
            ))}
          </CartaoKpi>

          <CartaoKpi
            rotulo="Capital social"
            valor={moeda(kpis.capitalTotal)}
            detalhe={`Ticket médio ${moeda(kpis.ticket)}`}
            icone={CircleDollarSign}
            tom="marca"
          />

          <CartaoKpi
            rotulo="Sócios"
            valor={String(kpis.sociosDistintos)}
            detalhe={`Média de ${kpis.mediaSocios.toFixed(1)} por sociedade`}
            icone={UsersRound}
            tom="neutro"
          />
        </div>
      )}

      {(sociedadesQuery.isError || pessoasQuery.isError) && (
        <Aviso>Não foi possível carregar as sociedades.</Aviso>
      )}

      {(!haDados || aba === 'dados') && (
        <Tabela
          colunas={colunas.filter((c) => !ocultas.includes(c.chave))}
          visao={visao}
          cartao={(s, acoesDaLinha) => (
            <CartaoSociedade sociedade={s} acoes={acoesDaLinha} />
          )}
          /*
            Página montada aqui: o recorte por período, busca e gaveta é feito
            em memória, sobre a leva inteira — pedir 25 ao servidor e filtrar
            depois mostraria 3 linhas numa página que anuncia 25.
          */
          pagina={{
            itens: daPagina,
            total: ordenados.length,
            pagina: paginaValida,
            totalPaginas,
            tamanho: tamanhoPagina,
          }}
          aoTrocarPagina={setPaginaAtual}
          aoTrocarTamanho={(n) => {
            setTamanhoPagina(n);
            // Trocar o tamanho recoloca na primeira página: manter a 7 ao
            // passar de 10 para 100 por página jogaria a pessoa para fora do
            // conjunto, e o que ela pediu foi ver MAIS, não ir adiante.
            setPaginaAtual(1);
          }}
          carregando={sociedadesQuery.isPending || pessoasQuery.isPending}
          chaveDe={(s) => s.id}
          ordenacao={ordenacao}
          aoOrdenar={(o) => {
            setOrdenacao(o);
            setPaginaAtual(1);
          }}
          /*
            O total somado ao lado da contagem: é a confirmação de que o filtro
            pegou o que a pessoa esperava. Some junto com os valores quando o
            olho está fechado — esconder o KPI e deixar o total no rodapé não
            esconderia nada.
          */
          rodape={
            kpisVisiveis && ordenados.length > 0 ? (
              <span className="text-tinta-500 text-sm">
                Capital total{' '}
                <b className="text-tinta-900 font-semibold tabular-nums">
                  {moeda(ordenados.reduce((t, s) => t + s.capitalCentavos, 0))}
                </b>
              </span>
            ) : undefined
          }
          vazio={vazio}
        />
      )}

      {haDados && aba === 'resumo' && <ResumoPorTipo sociedades={ordenados} />}
    </PaginaShell>
  );
}

/** Acessor de ordenação — cobre as colunas derivadas (`municipio`, `socios`). */
function valorOrdenavel(s: SociedadeResumo, chave: string): string | number {
  if (chave === 'municipio') return `${s.sede.municipio}/${s.sede.uf}`;
  if (chave === 'socios') return sociosAtuais(s);
  return (s as unknown as Record<string, unknown>)[chave] as string | number;
}
